import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Colors } from "../../../configs";
import CustomModal from "../../common/custom-modal/custom-modal";

interface TrayAppDownloadModalProps {
    open: boolean;
    onClose?: () => void;
    downloadUrl: string | null;
    version: string | null;
    loading: boolean;
    blocking?: boolean;
}

const TrayAppDownloadModal: React.FC<TrayAppDownloadModalProps> = ({
    open,
    onClose,
    downloadUrl,
    version,
    loading,
    blocking = false,
}) => {
    return (
        <CustomModal
            mode="modal"
            title="Presto Tray App Required"
            onClose={blocking ? undefined : onClose}
            openModal={open}
            width="44rem"
            height="auto"
            hideCloseButton={blocking}
            disableBackdropClick={blocking}
            backdropProps={{
                sx: {
                    backgroundColor: "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(0.25rem)",
                },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem", mt: 1, pb: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        backgroundColor: "#FFF3CD",
                        border: "1px solid #FBBF24",
                        borderRadius: "0.75rem",
                        px: "1rem",
                        py: "0.875rem",
                    }}
                >
                    <ErrorOutlineIcon sx={{ color: "#B45309", fontSize: "1.75rem", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.9rem", color: "#92400E", lineHeight: 1.6 }}>
                        The <strong>Presto Tray App</strong> is not running on this computer.
                        It is required for NFC card reading and thermal printer support.
                    </Typography>
                </Box>

                <Box sx={{ px: "0.25rem" }}>
                    <Typography sx={{ fontSize: "0.95rem", color: Colors.TEXT_GRAY_DARK, mb: "0.5rem", fontWeight: 600 }}>
                        How to install:
                    </Typography>
                    <Box component="ol" sx={{ m: 0, pl: "1.25rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                        {[
                            "Download the installer below",
                            "Extract the .zip file",
                            "Run PrestoTray.exe — the installer will open automatically",
                            "Click Install, then Launch",
                        ].map((step, i) => (
                            <Typography key={i} component="li" sx={{ fontSize: "0.875rem", color: Colors.TEXT_GRAY_DARK }}>
                                {step}
                            </Typography>
                        ))}
                    </Box>
                    <Typography sx={{ fontSize: "0.8rem", color: Colors.ACCENT_GRAY_DISABLED, mt: "0.75rem" }}>
                        Requires .NET 10.0 Runtime for Windows.
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: "0.5rem" }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Box
                        component="a"
                        href={downloadUrl ?? undefined}
                        download
                        rel="noopener noreferrer"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                            background: Colors.PRIMARY,
                            color: "#fff",
                            fontSize: "1rem",
                            fontWeight: 600,
                            padding: "0.75rem 1.5rem",
                            borderRadius: "0.75rem",
                            textDecoration: "none",
                            "&:hover": { background: Colors.PRIMARY_DARK },
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: "1.25rem" }} />
                        Download Presto Tray{version ? ` v${version}` : ""}
                    </Box>
                )}

                {!blocking && (
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: Colors.ACCENT_GRAY_DISABLED,
                            fontSize: "0.875rem",
                            alignSelf: "center",
                            p: 0,
                        }}
                    >
                        Continue without tray app
                    </Box>
                )}
            </Box>
        </CustomModal>
    );
};

export default TrayAppDownloadModal;
